import {useCallback, useEffect, useRef, useState} from 'react'

import styles from './RecentAgentActivity.module.css'

const REJECT_ANIMATION_FALLBACK_MS = 400

// Drives the reject-out animation shared by the pending chips. The reject is
// normally fired from the wrapper's opacity transitionEnd so the chip animates
// out first; a timeout fallback still fires it if the transition never completes
// (reduced motion, backgrounded tabs, rapid DOM changes).
export function useRejectAnimation(onReject: () => void): {
  wrapperClassName: string | undefined
  onWrapperTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void
  startReject: () => void
} {
  const [isRejecting, setIsRejecting] = useState(false)
  const hasFiredRejectRef = useRef(false)
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fireReject = useCallback(() => {
    if (hasFiredRejectRef.current) return
    hasFiredRejectRef.current = true
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }
    onReject()
  }, [onReject])

  useEffect(
    () => () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current)
    },
    [],
  )

  const startReject = useCallback(() => {
    setIsRejecting(true)
    fallbackTimeoutRef.current = setTimeout(fireReject, REJECT_ANIMATION_FALLBACK_MS)
  }, [fireReject])

  const onWrapperTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (isRejecting && event.propertyName === 'opacity' && event.target === event.currentTarget) {
        fireReject()
      }
    },
    [fireReject, isRejecting],
  )

  return {
    wrapperClassName: isRejecting ? styles.chipRejecting : undefined,
    onWrapperTransitionEnd,
    startReject,
  }
}
